/**
 * Extrait un message utilisateur à partir du tableau `errors` (format proche class-validator)
 * renvoyé par l’API sur les réponses 409 normalisées (`handleValidationError`).
 */

type ConstraintLeaf = { constraints?: Record<string, string> };

function isConstraintLeaf(value: unknown): value is ConstraintLeaf {
	return typeof value === "object" && value !== null && "constraints" in value;
}

export function userMessageFromValidationErrorsArray(errors: unknown): string | null {
	if (!Array.isArray(errors) || errors.length === 0) {
		return null;
	}
	const parts: string[] = [];
	for (const item of errors) {
		if (isConstraintLeaf(item) && item.constraints && typeof item.constraints === "object") {
			parts.push(...Object.values(item.constraints));
		}
	}
	return parts.length > 0 ? parts.join(" ") : null;
}

type FlatApiError = {
	http_status?: number;
	status?: number;
	message?: string;
	errors?: unknown;
	reason?: string;
	data?: FlatApiError;
};

/**
 * Contrat API : `message` est destiné à l’utilisateur ; `reason` est souvent technique (ex. détail d’échec amont).
 */
export function userFacingStringFromMessageThenReason(
	payload: { message?: unknown; reason?: unknown } | null | undefined,
): string | null {
	if (!payload || typeof payload !== "object") {
		return null;
	}
	if (typeof payload.message === "string" && payload.message.length > 0) {
		return payload.message;
	}
	if (typeof payload.reason === "string" && payload.reason.length > 0) {
		return payload.reason;
	}
	return null;
}

function messageIf409(errors: unknown, status: number | undefined): string | null {
	if (status !== 409) {
		return null;
	}
	return userMessageFromValidationErrorsArray(errors);
}

function messageFromNestedData(data: FlatApiError | undefined, parentStatus: number | undefined): string | null {
	if (!data || typeof data !== "object") {
		return null;
	}
	const nestedStatus = data.http_status ?? parentStatus;
	const fromErrors = messageIf409(data.errors, nestedStatus);
	if (fromErrors) {
		return fromErrors;
	}
	return userFacingStringFromMessageThenReason(data);
}

function messageFromTopLevel(e: FlatApiError): string | null {
	const status = e.http_status ?? e.status;
	const from409 = messageIf409(e.errors, status);
	if (from409) {
		return from409;
	}
	return null;
}

/**
 * Priorise les contraintes métier (`errors`) pour un 409 ; sinon `message` puis `reason` sous `data` (message = libellé utilisateur).
 */
export function userFacingTextFromNormalizedApiError(err: unknown): string | null {
	if (typeof err !== "object" || err === null) {
		return null;
	}
	const e = err as FlatApiError;
	const top = messageFromTopLevel(e);
	if (top) {
		return top;
	}
	const fromData = messageFromNestedData(e.data, e.http_status ?? e.status);
	if (fromData) {
		return fromData;
	}
	return userFacingStringFromMessageThenReason(e);
}
